<template>
  <div class="ai-customer-iframe">
    <!-- 加载中 -->
    <div v-if="status === 'loading'" class="state-wrap">
      <i class="el-icon-loading"></i>
      <span>正在加载素材库...</span>
    </div>

    <!-- 加载失败 -->
    <div v-else-if="status === 'error'" class="state-wrap">
      <i class="el-icon-warning state-icon"></i>
      <span class="state-text">{{ errorMsg || '素材库加载失败' }}</span>
      <el-button type="primary" size="small" @click="loadToken">重试</el-button>
    </div>

    <!-- iframe 整页嵌入 -->
    <iframe
      v-else-if="status === 'success' && iframeSrc"
      class="material-iframe"
      :src="iframeSrc"
      frameborder="0"
      allow="fullscreen"
      @load="onIframeLoad"
    ></iframe>
  </div>
</template>

<script>

  export default {
    name: 'ai-customer-iframe',
    props: {
      // iframe 类型：knowledge(知识库) / link(链接库) / library(素材库)
      type: { type: String, required: true },
      // 换新 token 的间隔（毫秒）。后端 mp-satoken 有效期约 1 分钟，默认提前留出安全余量
      tokenRefreshMs: { type: Number, default: 45000 },
    },
    data() {
      return {
        status: 'loading',        // loading / success / error
        errorMsg: '',
        mpsatoken: '',           // 从后端换出的 mpmanage 的 satoken（仅用于 iframe 首次加载）
        refreshTimer: null,       // token 定时换新 timer
      }
    },
    computed: {
      mpmanageHost() {
        return process.env.VUE_APP_MPMANAGE_HOST || ''
      },
      mpmanageEnv() {
        return process.env.VUE_APP_MPMANAGE_ENV || 'pro'
      },
      /**
       * iframe src 拼接：
       * - type: 由调用方传入，区分 knowledge / link / library
       * - token: mpmanage 的 mp-satoken
       * - parentOrigin: 本页 origin，用于 mpmanage 侧精准 postMessage
       */
      iframeSrc() {
        if (!this.mpmanageHost || !this.mpsatoken) return ''
        const params = new URLSearchParams({
          type: this.type,
          token: this.mpsatoken,
          parentOrigin: window.location.origin,
        })
        return `${this.mpmanageHost}/materialPicker?${params.toString()}`
      },
    },
    methods: {
      async loadToken() {
        this.status = 'loading'
        this.errorMsg = ''
        try {
          const res = await this.$http.getBjAiTokenAPI()
          if (res && res.status === '1') {
            const data = res.data || {}
            const token = data.satoken
            if (!token) {
              throw new Error('未获取到 satoken')
            }
            this.mpsatoken = token
            this.status = 'success'
          } else {
            throw new Error(res && res.msg || res.context || '获取 satoken 失败')
          }
        } catch (e) {
          this.errorMsg = (e && e.message) || '获取 satoken 失败'
          this.status = 'error'
        }
      },
      onIframeLoad() {
        // iframe 首次加载完成，启动 token 定时换新循环
        // this.startTokenRefresh()
      },
      /**
       * 从后端换新 token 并通过 postMessage 推给 iframe
       * 注意：绝不更新 this.mpsatoken，否则 iframeSrc 变化会导致 iframe 重载、丢失用户操作状态
       */
      async refreshTokenOnce() {
        if (this.status !== 'success') return false
        try {
          const res = await  this.$http.getBjAiTokenAPI()
          if (res && res.status === '1') {
            const data = res.data || {}
            const token = data.satoken
            if (token) {
              this.postMessageToIframe({ type: 'qimai-material-token', data: { token } })
              return true
            }
          }
        } catch (e) {
          // 静默失败，下个周期重试；当前旧 token 可能仍在有效期内
        }
        return false
      },
      startTokenRefresh() {
        this.stopTokenRefresh()
        this.refreshTimer = setInterval(() => {
          this.refreshTokenOnce()
        }, this.tokenRefreshMs)
      },
      stopTokenRefresh() {
        if (this.refreshTimer) {
          clearInterval(this.refreshTimer)
          this.refreshTimer = null
        }
      },
      /**
       * 回到前台时立即换新一次，防止浏览器后台节流 setInterval 导致过期窗口
       */
      onVisibilityChange() {
        if (document.visibilityState === 'visible') {
          this.refreshTokenOnce()
        }
      },
      postMessageToIframe(msg) {
        const iframe = this.$el.querySelector('.material-iframe')
        if (!iframe || !iframe.contentWindow) return
        let target = '*'
        try {
          if (this.mpmanageHost) target = new URL(this.mpmanageHost).origin
        } catch (e) { /* ignore */ }
        iframe.contentWindow.postMessage(msg, target)
      },
    },
    created() {
      this.loadToken()
    },
    mounted() {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    },
    beforeDestroy() {
      this.stopTokenRefresh()
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    },
  }
</script>

<style lang="scss" scoped>
  .ai-customer-iframe {
    width: 100%;
    height: 100vh;
    background: #fff;

    .material-iframe {
      width: 100%;
      height: 100%;
      border: 0;
      display: block;
    }

    .state-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      min-height: 400px;
      color: #909399;
      font-size: 14px;

      .state-icon {
        font-size: 40px;
        margin-bottom: 12px;
        color: #e6a23c;
      }

      .state-text {
        margin-bottom: 16px;
      }

      i.el-icon-loading {
        font-size: 28px;
        margin-bottom: 12px;
      }
    }
  }
</style>
